import {
  Badge,
  Box,
  Button,
  Card,
  Flex,
  Grid,
  Icon,
  Text,
} from '@chakra-ui/react'
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { fetchInterviewList, deleteInterview } from '../../api/recruitment/recruitment'
import { PlusIcon, Trash04Icon } from '../../assets/icons'
import { INTERVIEW_TYPE_LABELS } from '../../types/recruitment/recruitmentTypes'
import { useFormMutation } from '../../hooks/useFormMutation'
import AvatarCell from './base/AvatarCell'
import LoadingTable from './base/LoadingTable'
import MasterTable, { Column, RowData } from './base/MasterTable'
import Pagination from './base/Pagination'
import SearchInput from './base/SearchInput'
import FilterButton from './base/FilterButton'
import ScheduleInterviewModal from '../modals/Recruitment/ScheduleInterviewModal'

const InterviewsTable: React.FC = () => {
  const { t } = useTranslation()
  const [page, setPage] = useState(1)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedRows, setSelectedRows] = useState<string[]>([])
  const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false)
  const [filters, setFilters] = useState<{ [key: string]: string[] }>({})
  const queryClient = useQueryClient()
  const itemsPerPage = 15
  const filtersKey = JSON.stringify(filters)

  const interviews = useQuery({
    queryKey: ['interviews', page, searchTerm, filtersKey],
    staleTime: 60 * 1000,
    placeholderData: keepPreviousData,
    queryFn: () =>
      fetchInterviewList({
        search: searchTerm,
        page,
        pageSize: itemsPerPage,
        filters,
      }),
  })

  React.useEffect(() => {
    const currentCount = interviews.data?.data?.length ?? 0
    if (currentCount < itemsPerPage) return

    const nextPage = page + 1

    queryClient.prefetchQuery({
      queryKey: ['interviews', nextPage, searchTerm, filtersKey],
      staleTime: 60 * 1000,
      queryFn: () =>
        fetchInterviewList({
          search: searchTerm,
          page: nextPage,
          pageSize: itemsPerPage,
          filters,
        }),
    })
  }, [interviews.data, page, searchTerm, filters, filtersKey, itemsPerPage, queryClient])

  const deleteMutation = useFormMutation({
    mutationFn: deleteInterview,
    queryKey: 'interviews',
    onSuccess: () => setSelectedRows([]),
  })

  const tableData = interviews?.data?.data || []

  const statusFilterOptions = [
    { id: 'scheduled', name: 'Scheduled' },
    { id: 'completed', name: 'Completed' },
    { id: 'cancelled', name: 'Cancelled' },
  ]

  const typeFilterOptions = [
    { id: 'phone', name: 'Phone' },
    { id: 'video', name: 'Video' },
    { id: 'onsite', name: 'On-site' },
  ]

  const updateFilter = (key: string, values: string[]) => {
    setFilters((prev) => {
      const next = { ...prev }
      if (values.length === 0) delete next[key]
      else next[key] = values
      return next
    })
    setPage(1)
  }

  const interviewStatusColors: Record<string, { bg: string; text: string }> = {
    scheduled: { bg: 'primary.0', text: 'primary.200' },
    completed: { bg: 'success.0', text: 'success.200' },
    cancelled: { bg: 'error.0', text: 'error.200' },
  }

  const columns: Column[] = [
    { label: '', accessor: 'select', type: 'select', width: '5%' },
    {
      label: t('common.columns.candidate'),
      accessor: 'candidate',
      type: 'string',
      Cell: ({ row }: { row: RowData }) => {
        const candidate = row.application?.candidate
        return (
          <AvatarCell name={candidate ? `${candidate.first_name} ${candidate.last_name}` : 'N/A'} />
        )
      },
    },
    {
      label: t('common.columns.interviewer'),
      accessor: 'interviewer',
      type: 'string',
      Cell: ({ row }: { row: RowData }) => {
        const interviewer = row.interviewer && typeof row.interviewer === 'object' ? row.interviewer : null
        return (
          <AvatarCell image={interviewer?.profile_picture} name={interviewer ? `${interviewer.first_name} ${interviewer.last_name}` : 'N/A'} />
        )
      },
    },
    {
      label: t('common.columns.dateTime'),
      accessor: 'scheduled_at',
      type: 'string',
      Cell: ({ row }: { row: RowData }) => (
        <Text textStyle="l-xs">
          {row.scheduled_at
            ? new Date(row.scheduled_at).toLocaleString(undefined, {
                dateStyle: 'medium',
                timeStyle: 'short',
              })
            : 'N/A'}
        </Text>
      ),
    },
    {
      label: t('common.fields.type'),
      accessor: 'type',
      type: 'string',
      Cell: ({ row }: { row: RowData }) => (
        <Text textStyle="l-xs" textTransform="capitalize">
          {INTERVIEW_TYPE_LABELS[row.type as keyof typeof INTERVIEW_TYPE_LABELS] || row.type}
        </Text>
      ),
    },
    {
      label: t('common.columns.duration'),
      accessor: 'duration_minutes',
      type: 'string',
      Cell: ({ row }: { row: RowData }) => (
        <Text textStyle="l-xs">{row.duration_minutes ? `${row.duration_minutes} min` : 'N/A'}</Text>
      ),
    },
    {
      label: t('common.fields.status'),
      accessor: 'status',
      type: 'custom',
      Cell: ({ row }: { row: RowData }) => {
        const colors = interviewStatusColors[row.status] || { bg: 'solid.50', text: 'solid.600' }
        return (
          <Badge
            bg={colors.bg}
            color={colors.text}
            px="10px"
            py="4px"
            borderRadius="8px"
            fontSize="11px"
            fontWeight="600"
            textTransform="capitalize"
            border="1px solid"
            borderColor={colors.text}
            display="inline-flex"
            w="85px"
            alignItems="center"
            justifyContent="center"
          >
            {row.status}
          </Badge>
        )
      },
    },
    {
      label: t('common.columns.rating'),
      accessor: 'rating',
      type: 'string',
      Cell: ({ row }: { row: RowData }) => (
        <Text textStyle="l-xs" color={row.rating ? 'solid.900' : 'solid.300'}>
          {row.rating ? `${row.rating}/5` : '-'}
        </Text>
      ),
    },
    { label: '', accessor: 'actions', type: 'actions' },
  ]

  const handleSearch = (term: string) => {
    setPage(1)
    setSearchTerm(term)
  }

  const handleSelectRow = (id: string | number, isChecked: boolean) => {
    const strId = String(id)
    if (isChecked) setSelectedRows((prev) => [...prev, strId])
    else setSelectedRows((prev) => prev.filter((rowId) => rowId !== strId))
  }

  const handleSelectAll = (isChecked: boolean) => {
    if (isChecked) setSelectedRows(tableData.map((row: RowData) => String(row.id)))
    else setSelectedRows([])
  }

  const handleDelete = async () => {
    try {
      await Promise.all(selectedRows.map((id) => deleteMutation.mutateAsync(id)))
      setSelectedRows([])
    } catch {
      // partial failure — list will refetch via onSuccess of individual mutations
    }
  }

  return (
    <Card width="100%" p="0">
      <Grid>
        <Flex justifyContent="space-between" alignItems="center" height="64px" flexWrap="wrap" gap={4} minW={0} px="20px">
          <SearchInput onSearch={handleSearch} />
          <Flex gap="8px" flexShrink={0}>
            {selectedRows.length > 0 ? (
              <Button onClick={handleDelete} variant="error" size="sm" h="32px" px="14px" fontSize="13px" fontWeight="600" borderRadius="8px">
                <Icon fill="none" color="error.100" mr={2}><Trash04Icon /></Icon>
                Cancel ({selectedRows.length})
              </Button>
            ) : (
              <>
                <FilterButton label={t('common.fields.status')} options={statusFilterOptions} onApply={(selected) => updateFilter('status', selected)} />
                <FilterButton label={t('common.fields.type')} options={typeFilterOptions} onApply={(selected) => updateFilter('type', selected)} />
                <Button variant="mono" onClick={() => setIsScheduleModalOpen(true)} size="sm" h="32px" px="14px" fontSize="13px" fontWeight="600" borderRadius="8px">
                  <Icon w="12px" h="12px" viewBox="0 0 24 24" fill="none" color="white"><PlusIcon /></Icon>
                  {t('recruitment.actions.scheduleInterview')}
                </Button>
              </>
            )}
          </Flex>
        </Flex>
        {interviews.isLoading ? (
          <LoadingTable columns={columns} />
        ) : (
          <Box minHeight="58vh" overflowX="auto" overflowY="visible">
            <MasterTable
              columns={columns}
              data={tableData}
              selectedRows={selectedRows}
              onSelectAll={handleSelectAll}
              onSelectRow={handleSelectRow}
              allSelected={tableData.length > 0 && selectedRows.length === tableData.length}
              isIndeterminate={tableData.length > 0 && selectedRows.length > 0 && selectedRows.length < tableData.length}
              tableType="people"
            />
          </Box>
        )}
        <Pagination
          currentPage={page}
          itemsPerPage={itemsPerPage}
          maxPage={interviews?.data?.max_page || 0}
          totalItems={interviews?.data?.count || 0}
          handlePreviousPage={() => setPage(page - 1)}
          handleNextPage={() => setPage(page + 1)}
          handlePageClick={(p: number) => setPage(p)}
        />
      </Grid>
      <ScheduleInterviewModal isOpen={isScheduleModalOpen} onClose={() => setIsScheduleModalOpen(false)} />
    </Card>
  )
}

export default InterviewsTable
